import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Paper,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import intercomService from '../../services/intercom.service';
import Jiraintercomdescription from '../../components/jira/jiraintercomdescription';
import styles from './Intercom.module.css';

const formatTimestamp = (timestamp) => {
	if (!timestamp) {
		return '';
	}
	const date = new Date(timestamp * 1000);
	if (Number.isNaN(date.getTime())) {
		return '';
	}
	return date.toLocaleString(undefined, {
		year: 'numeric',
		month: 'short',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		timeZoneName: 'short',
	});
};

const formatElapsed = (currentTimestamp, previousTimestamp) => {
	if (!currentTimestamp || !previousTimestamp) {
		return '';
	}
	const deltaSeconds = Math.max(0, currentTimestamp - previousTimestamp);
	const hours = Math.floor(deltaSeconds / 3600);
	const minutes = Math.floor((deltaSeconds % 3600) / 60);
	const seconds = deltaSeconds % 60;
	if (hours > 0) {
		return `${hours}h ${minutes}m`;
	}
	if (minutes > 0) {
		return `${minutes}m ${seconds}s`;
	}
	return `${seconds}s`;
};

const getAuthorLabel = (author) => {
	if (!author) {
		return 'Unknown';
	}
	return author.name || author.email || author.id || 'Unknown';
};

const IntercomTicketDetail = () => {
	const navigate = useNavigate();
	const { id } = useParams();
	const [ticket, setTicket] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [errorMessage, setErrorMessage] = useState('');
	const [isEscalating, setIsEscalating] = useState(false);
	const [escalateMessage, setEscalateMessage] = useState('');
	const [escalateError, setEscalateError] = useState('');
	const [isEscalateDialogOpen, setIsEscalateDialogOpen] = useState(false);
	const [escalateReason, setEscalateReason] = useState('');
	const [escalateDialogError, setEscalateDialogError] = useState('');

	useEffect(() => {
		document.title = 'Intercom Ticket - GSD Support Admin';
		return () => {
			document.title = 'GSD Support Admin';
		};
	}, []);

	const loadTicket = useCallback(async () => {
		if (!id) {
			return;
		}
		setIsLoading(true);
		setErrorMessage('');
		try {
			const data = await intercomService.getTicketById(id);
			setTicket(data?.result || null);
		} catch (error) {
			setErrorMessage(error?.message || 'Failed to load ticket.');
		} finally {
			setIsLoading(false);
		}
	}, [id]);

	useEffect(() => {
		loadTicket();
	}, [loadTicket]);

	const messages = useMemo(() => {
		if (!ticket) {
			return [];
		}

		const parts = ticket?.ticket_parts?.ticket_parts || [];
		return parts
			.filter((part) => part?.body)
			.map((part) => ({
				id: part.id,
				body: part.body,
				created_at: part.created_at,
				author: part.author,
				part_type: part.part_type,
			}))
			.sort((a, b) => (a.created_at || 0) - (b.created_at || 0));
	}, [ticket]);

	const isEscalated = useMemo(() => {
		const parts = ticket?.ticket_parts?.ticket_parts || [];
		return parts.some((part) => {
			if (!part?.body) {
				return false;
			}
			return part.body.toLowerCase().includes('gsd_escalate');
		});
	}, [ticket]);

	const isTicketOpen = ticket?.open === true || ticket?.open === 'true';

	const handleBack = () => {
		if (window.history.length > 1) {
			navigate(-1);
		} else {
			navigate('/intercom/conversations');
		}
	};

	const handleEscalate = async (reason) => {
		if (!id || isEscalating) {
			return;
		}
		setIsEscalating(true);
		setEscalateMessage('');
		setEscalateError('');
		setEscalateDialogError('');
		try {
			const response = await intercomService.escalateTicket(id, reason);
			setEscalateMessage(response?.message || 'Ticket escalated successfully.');
			setIsEscalateDialogOpen(false);
			setEscalateReason('');
			await loadTicket();
		} catch (error) {
			setEscalateError(error?.message || 'Failed to escalate ticket.');
			setEscalateDialogError(error?.message || 'Failed to escalate ticket.');
		} finally {
			setIsEscalating(false);
		}
	};

	const openEscalateDialog = () => {
		if (isEscalated || isEscalating || !id || !isTicketOpen) {
			return;
		}
		setEscalateMessage('');
		setEscalateError('');
		setEscalateDialogError('');
		setIsEscalateDialogOpen(true);
	};

	const closeEscalateDialog = () => {
		if (isEscalating) {
			return;
		}
		setIsEscalateDialogOpen(false);
		setEscalateDialogError('');
	};

	const submitEscalation = () => {
		const trimmedReason = escalateReason.trim();
		if (!trimmedReason) {
			setEscalateDialogError('Please provide a reason to escalate.');
			return;
		}
		handleEscalate(trimmedReason);
	};

	const ticketTitle = ticket?.ticket_attributes?._default_title_ || `Ticket ${ticket?.ticket_id || id}`;
	const ticketDescription = ticket?.ticket_attributes?._default_description_ || '';
	const ticketState = ticket?.ticket_state?.external_label || ticket?.ticket_state?.category || 'unknown';

	return (
		<div className={styles.divStyle}>
			<div className={styles.sectionContainer}>
				<div className={styles.sectionHeader}>
					<Tooltip title="Intercom Tickets">
						<button
							type="button"
							className={styles.backButton}
							onClick={handleBack}
						>
							<ArrowBackIcon className={styles.backIcon} />
						</button>
					</Tooltip>
					<div>
						<div className={styles.sectionTitleRow}>
							<h1 className={styles.sectionTitle}>Ticket {ticket?.ticket_id || id}</h1>
							<Chip label={ticketState} color="primary" size="small" />
						</div>
						<p className={styles.sectionDescription}>
							Review ticket details and message history.
						</p>
					</div>
				</div>
			</div>

			<Paper className={styles.detailContainer}>
				{isLoading ? (
					<Box className={styles.loadingState}>
						<CircularProgress />
					</Box>
				) : errorMessage ? (
					<Typography color="error">{errorMessage}</Typography>
				) : ticket ? (
					<>
						<Box className={styles.detailHeader}>
							<Box>
								<Typography variant="h6">{ticketTitle}</Typography>
								{ticketDescription && (
									<Typography variant="body2" color="text.secondary">
										{ticketDescription}
									</Typography>
								)}
							</Box>
							{isTicketOpen && (
								<Box className={styles.detailHeaderActions}>
									<Button
										variant="contained"
										size="small"
										onClick={openEscalateDialog}
										disabled={isEscalating || !id || isEscalated}
										color={isEscalated ? 'success' : 'primary'}
										sx={
											isEscalated
												? {
													backgroundColor: 'success.main',
													color: 'success.contrastText',
													'&.Mui-disabled': {
														backgroundColor: 'success.main',
														color: 'success.contrastText',
														opacity: 1,
													},
												}
												: undefined
										}
									>
										{isEscalated ? 'Escalated' : isEscalating ? 'Escalating...' : 'Escalate'}
									</Button>
								</Box>
							)}
						</Box>

						{(escalateMessage || escalateError) && (
							<Box className={styles.detailActions}>
								<Typography sx={{ color: escalateError ? 'error.main' : 'success.main' }}>
									{escalateError || escalateMessage}
								</Typography>
							</Box>
						)}

						<Box className={styles.detailMeta}>
							<Typography variant="body2">Created: {formatTimestamp(ticket.created_at)}</Typography>
							<Typography variant="body2">Updated: {formatTimestamp(ticket.updated_at)}</Typography>
							<Typography variant="body2">
								Type: {ticket.ticket_type?.name || 'unknown'}
							</Typography>
							<Typography variant="body2">
								Category: {ticket.category || ticket.ticket_type?.category || 'unknown'}
							</Typography>
							<Typography variant="body2">
								Channel: {ticket.channel || 'unknown'}
							</Typography>
						</Box>

						<div className={styles.chatContainer}>
							{messages.length === 0 ? (
								<Typography variant="body2" color="text.secondary">
									No ticket messages available.
								</Typography>
							) : (
								messages.map((message, index) => {
									const previousMessage = messages[index - 1];
									const elapsed = formatElapsed(
										message.created_at,
										previousMessage?.created_at
									);
									const isUser = message?.author?.type === 'user';
									return (
										<div
											key={message.id}
											className={`${styles.messageRow} ${isUser ? styles.messageLeft : styles.messageRight}`}
										>
											<div className={styles.messageBubble}>
												<div className={styles.messageMeta}>
													<span className={styles.messageAuthor}>{getAuthorLabel(message.author)}</span>
													<span className={styles.messageTime}>
														{formatTimestamp(message.created_at)}
														{elapsed ? ` (+${elapsed})` : ''}
													</span>
												</div>
												<div className={styles.messageBody}>
													<Jiraintercomdescription data={message.body} />
												</div>
												{message.part_type && (
													<div className={styles.messageType}>{message.part_type}</div>
												)}
											</div>
										</div>
									);
								})
							)}
						</div>
					</>
				) : (
					<Typography>No ticket data found.</Typography>
				)}
			</Paper>

			<Dialog
				open={isEscalateDialogOpen}
				onClose={closeEscalateDialog}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>Escalate Ticket</DialogTitle>
				<DialogContent dividers>
					<TextField
						label="Reason to escalate"
						required
						value={escalateReason}
						onChange={(event) => {
							setEscalateReason(event.target.value);
							if (escalateDialogError) {
								setEscalateDialogError('');
							}
						}}
						placeholder="Provide a brief reason for escalation"
						fullWidth
						multiline
						minRows={3}
						autoFocus
					/>
					{escalateDialogError && (
						<Typography sx={{ color: 'error.main', mt: 1 }}>
							{escalateDialogError}
						</Typography>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={closeEscalateDialog} disabled={isEscalating}>
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={submitEscalation}
						disabled={isEscalating}
					>
						{isEscalating ? 'Escalating...' : 'Escalate'}
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default IntercomTicketDetail;
